<template>
    <admin-page :result="result" description="Revive una cuenta eliminada dentro de los 30 días de gracia">
        <div class="form-row">
            <kimsa-text-field class="form-field" @change="onChangeEmail" :value="email" label="Email de la cuenta"
                placeholder="usuario@correo.com" dense hide-details />
            <v-btn color="secondary" class="form-btn" depressed :loading="searching" :disabled="!email"
                @click="searchAccount">
                Buscar
            </v-btn>
        </div>

        <div v-if="notFound" class="restore-empty">
            No hay ninguna cuenta eliminada con ese email.
        </div>

        <div v-if="deletedAccount" class="restore-card">
            <div class="restore-head">
                <div class="restore-name">{{ deletedAccount.name || 'Sin nombre' }}</div>
                <span class="restore-status" :class="`restore-status--${deletedAccount.status}`">{{ statusLabel }}</span>
            </div>
            <div class="restore-fields">
                <div class="restore-field">
                    <span>Email</span>
                    <b>{{ deletedAccount.email }}</b>
                </div>
                <div class="restore-field">
                    <span>Tipo de cuenta</span>
                    <b>{{ accountTypeLabel }}</b>
                </div>
                <div class="restore-field">
                    <span>Eliminada</span>
                    <b>{{ deletedAtLabel }}</b>
                </div>
                <div class="restore-field">
                    <span>Gracia restante</span>
                    <b :class="daysLeft > 0 ? null : 'error--text'">
                        {{ daysLeft > 0 ? `${daysLeft} día(s)` : 'Vencida' }}
                    </b>
                </div>
            </div>

            <div v-if="canRestore" class="restore-note">
                <v-icon size="16" color="accent">mdi-information-outline</v-icon>
                <span>La suscripción de Stripe no se restaura: si pagaba, tiene que volver a elegir plan.</span>
            </div>

            <v-btn color="primary" class="black--text restore-btn mt-4" depressed block :loading="loading"
                :disabled="!canRestore" @click="$refs.confirm.show()">
                Restaurar cuenta
            </v-btn>
        </div>

        <kimsa-confirm ref="confirm" show-cancel title="Restaurar cuenta" :content="confirmContent"
            :persistent="false" :close-on-confirm="false" :loading="loading" @confirm="restoreAccount" />
    </admin-page>
</template>

<script>
import firebase from 'firebase/app'
import 'firebase/functions'
import KimsaTextField from '@/components/common/inputs/KimsaTextField.vue'
import KimsaConfirm from '@/components/common/KimsaConfirm.vue'
import AdminPage from '@/components/admin/AdminPage.vue'
import { db } from '@/utils/Firebase'
import { adminRouteGuard } from '@/utils/Admin'

const GRACE_DAYS = 30

const ACCOUNT_TYPES = {
    student: 'Alumno',
    coach: 'Entrenador',
    coach_company: 'Entrenador de empresa',
    company: 'Empresa',
}

export default {
    name: 'AdminRestoreUser',
    components: { KimsaTextField, KimsaConfirm, AdminPage },
    beforeRouteEnter: adminRouteGuard,
    data() {
        return {
            email: '',
            deletedAccount: null,
            notFound: false,
            searching: false,
            loading: false,
            result: null,
        }
    },
    computed: {
        accountTypeLabel(vm = this) {
            return ACCOUNT_TYPES[vm.deletedAccount?.accountType] || 'Desconocido'
        },
        statusLabel(vm = this) {
            const labels = { 'soft-deleted': 'Recuperable', purged: 'Purgada', restored: 'Ya restaurada' }
            return labels[vm.deletedAccount?.status] || vm.deletedAccount?.status || ''
        },
        deletedAtLabel(vm = this) {
            const date = vm.deletedAccount?.deletedAt
            if (!date) return '—'
            return vm.$moment(date.toDate ? date.toDate() : date).format('DD/MM/YYYY HH:mm')
        },
        daysLeft(vm = this) {
            const date = vm.deletedAccount?.deletedAt
            if (!date) return 0
            const deleted = vm.$moment(date.toDate ? date.toDate() : date)
            const elapsed = Math.floor((Date.now() - deleted.valueOf()) / 86400000)
            return Math.max(0, GRACE_DAYS - elapsed)
        },
        canRestore(vm = this) {
            return vm.deletedAccount?.status === 'soft-deleted' && vm.daysLeft > 0
        },
        confirmContent(vm = this) {
            return `Vas a restaurar la cuenta de <b>${vm.deletedAccount?.email || ''}</b>: se rehabilita el acceso y vuelve su perfil con los datos que tenía al borrarse.`
        },
    },
    mounted() {
        const vm = this
        if (!vm.$route.query.email) return
        vm.email = vm.$route.query.email
        vm.searchAccount()
    },
    methods: {
        onChangeEmail(email) {
            const vm = this
            vm.email = email
            vm.deletedAccount = null
            vm.notFound = false
            vm.result = null
        },
        async searchAccount() {
            const vm = this
            vm.deletedAccount = null
            vm.notFound = false
            vm.result = null
            await vm.withLoading(async () => {
                const snapshot = await db.collection('deletedAccounts')
                    .where('email', '==', vm.email.trim().toLowerCase())
                    .limit(1)
                    .get()

                if (snapshot.empty) return vm.notFound = true
                vm.deletedAccount = { id: snapshot.docs[0].id, ...snapshot.docs[0].data() }
            }, 'searching')
        },
        async restoreAccount() {
            const vm = this
            vm.loading = true
            vm.result = null
            try {
                const fn = firebase.functions().httpsCallable('httpAdmin-restoreUser')
                const { data } = await fn({ email: vm.deletedAccount.email })
                vm.result = {
                    type: 'success',
                    message: `Cuenta de ${vm.deletedAccount.email} restaurada (uid ${data.uid})${data.enabledAuth ? '' : ', pero no se pudo rehabilitar el acceso en Auth'}.`
                }
                vm.deletedAccount = null
                vm.email = ''
            } catch (err) {
                console.error('[adminRestoreUser] error', err)
                vm.result = {
                    type: 'error',
                    message: err.message || 'Error al restaurar la cuenta.'
                }
            } finally {
                vm.loading = false
                vm.$refs.confirm?.hide()
            }
        },
    },
}
</script>

<style scoped>
.form-row {
    display: flex;
    align-items: flex-end;
    gap: 12px;
    flex-wrap: wrap;
}

.form-field {
    flex: 1;
    min-width: 240px;
}

.form-btn {
    height: 40px !important;
    min-width: 120px;
    border-radius: 8px;
}

.restore-empty {
    margin-top: 16px;
    font-size: 13px;
    color: var(--v-txtMuted-base);
}

.restore-card {
    margin-top: 20px;
    padding: 16px;
    border-radius: 12px;
    background: var(--v-surface2-base);
    border: 1px solid var(--v-outline-base);
}

.restore-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    margin-bottom: 12px;
}

.restore-name {
    font-size: 15px;
    font-weight: 700;
    color: var(--v-txtPrimary-base);
}

.restore-status {
    font-size: 11px;
    font-weight: 700;
    padding: 3px 10px;
    border-radius: 20px;
}

.restore-status--soft-deleted {
    background: rgba(0, 153, 102, 0.14);
    color: var(--v-success-base);
}

.restore-status--purged,
.restore-status--restored {
    background: rgba(136, 146, 164, 0.14);
    color: var(--v-txtMuted-base);
}

.restore-fields {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px 16px;
}

.restore-field {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 10px;
    font-size: 12px;
    color: var(--v-txtMuted-base);
    border-bottom: 1px solid var(--v-outline-base);
    padding-bottom: 6px;
}

.restore-field b {
    color: var(--v-txtPrimary-base);
    font-weight: 600;
    text-align: right;
}

.restore-note {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    margin-top: 14px;
    font-size: 12px;
    line-height: 1.5;
    color: var(--v-txtSecondary-base);
}

.restore-btn {
    height: 40px !important;
    border-radius: 8px;
}

@media (max-width: 600px) {
    .restore-fields {
        grid-template-columns: 1fr;
    }

    .form-btn {
        width: 100%;
    }
}
</style>
